import React, { useState } from 'react';
import {
  CheckCircleIcon,
  DocumentArrowDownIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { formatearMoneda } from '../../utils/formatters';
import type { GenerateReceiptResult, ReciboFacturaContext } from '../../utils/reciboPagoService';
import {
  generateWhatsAppReceiptMessage,
  openWhatsAppWithReceipt,
  getClientPhoneForWhatsApp,
} from '../../utils/reciboPagoService';
import { downloadReciboPdfBlob, canSharePdfFiles, shareReciboPdfBlob } from '../../utils/reciboPagoPdfBlob';

export type ReciboPagoSuccessProps = {
  facturaRef: ReciboFacturaContext;
  companyName: string;
  monto: number;
  saldoAnterior: number;
  saldoDespues: number;
  receipt: GenerateReceiptResult | null;
  generating?: boolean;
  onClose: () => void;
  heading?: string;
};

export function ReciboPagoGenerating() {
  return (
    <div className="px-4 py-8 flex flex-col items-center justify-center text-center">
      <ArrowPathIcon className="w-12 h-12 text-[#1A56DB] animate-spin mb-4" />
      <p className="text-gray-700 font-medium">Generando recibo de pago...</p>
      <p className="text-sm text-gray-500 mt-1">Esto tomará unos segundos</p>
    </div>
  );
}

export function ReciboPagoSuccessPanel({
  facturaRef,
  companyName,
  monto,
  saldoAnterior,
  saldoDespues,
  receipt,
  onClose,
  heading = '¡Abono registrado!',
}: ReciboPagoSuccessProps) {
  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);
  const clientPhone = getClientPhoneForWhatsApp(facturaRef);

  const handleDownload = () => {
    if (receipt) downloadReciboPdfBlob(receipt.pdfBlob, receipt.filename);
  };

  const handleShare = async () => {
    if (!receipt) return;
    if (canSharePdfFiles()) {
      try {
        await shareReciboPdfBlob(receipt.pdfBlob, receipt.filename, {
          title: `Recibo ${receipt.reciboNumero}`,
          text: `Recibo de pago - ${companyName}`,
        });
        return;
      } catch {
        /* fall through */
      }
    }
    downloadReciboPdfBlob(receipt.pdfBlob, receipt.filename);
  };

  const handleSendWhatsApp = () => {
    if (!receipt?.reciboUrl || !clientPhone) return;
    setSendingWhatsApp(true);
    try {
      const message = generateWhatsAppReceiptMessage(
        facturaRef,
        receipt.reciboNumero,
        monto,
        saldoDespues,
        receipt.reciboUrl,
        companyName
      );
      openWhatsAppWithReceipt(clientPhone, message, '57');
    } finally {
      setSendingWhatsApp(false);
    }
  };

  return (
    <div className="px-4 py-4 space-y-4">
      <div className="flex flex-col items-center text-center py-2">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-3">
          <CheckCircleIcon className="w-10 h-10 text-green-600" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900">{heading}</h3>
        <p className="text-2xl font-bold text-green-600 mt-1">{formatearMoneda(monto)}</p>
      </div>

      <div className="bg-gray-50 rounded-lg p-3 space-y-2 text-sm">
        {facturaRef.numero && facturaRef.numero !== 'Cobro' && (
          <div className="flex justify-between">
            <span className="text-gray-600">Factura</span>
            <span className="font-medium">{facturaRef.numero}</span>
          </div>
        )}
        <div className="flex justify-between">
          <span className="text-gray-600">Saldo anterior</span>
          <span className="font-medium">{formatearMoneda(saldoAnterior)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">Abono</span>
          <span className="font-medium text-green-600">- {formatearMoneda(monto)}</span>
        </div>
        <div className="border-t pt-2 flex justify-between">
          <span className="text-gray-700 font-medium">
            {saldoDespues > 0 ? 'Nuevo saldo' : 'Cuenta saldada'}
          </span>
          <span className={`font-bold ${saldoDespues > 0 ? 'text-orange-600' : 'text-green-600'}`}>
            {formatearMoneda(saldoDespues)}
          </span>
        </div>
      </div>

      {receipt ? (
        <div className="space-y-2">
          <p className="text-xs text-gray-500 text-center">Recibo N° {receipt.reciboNumero}</p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleDownload}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              <DocumentArrowDownIcon className="w-5 h-5" />
              Descargar
            </button>
            {clientPhone && receipt.reciboUrl ? (
              <button
                type="button"
                onClick={handleSendWhatsApp}
                disabled={sendingWhatsApp}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
                </svg>
                WhatsApp
              </button>
            ) : null}
          </div>
          {!clientPhone && (
            <p className="text-xs text-gray-500 text-center">No hay teléfono del cliente para WhatsApp</p>
          )}
          {clientPhone && !receipt.reciboUrl && (
            <p className="text-xs text-amber-700 text-center">
              Descarga el PDF para compartirlo. WhatsApp solo se habilita cuando hay un enlace del recibo.
            </p>
          )}
          <button
            type="button"
            onClick={() => void handleShare()}
            className="w-full text-xs text-gray-500 hover:text-gray-700 py-1"
          >
            Compartir PDF…
          </button>
        </div>
      ) : (
        <p className="text-xs text-gray-500 text-center">
          El abono quedó registrado. El recibo no pudo generarse; la factura se actualizó con el nuevo saldo.
        </p>
      )}

      <button
        type="button"
        onClick={onClose}
        className="w-full px-4 py-2.5 text-sm font-medium text-white rounded-lg bg-[#1A56DB] hover:bg-[#1547b8]"
      >
        Cerrar
      </button>
    </div>
  );
}
